<template>
  <BaseModal
    :model-value="modelValue"
    :title-id="titleId"
    card-class="bulk-delete-modal"
    @update:model-value="handleVisibilityChange"
  >
    <template #header>
      <p :id="titleId" class="modal-card-title">Permanently delete {{ entityLabel }}s</p>
    </template>

    <template #default>
      <p class="mb-4">
        You selected <strong>{{ selectedCount }}</strong> {{ entityLabel
        }}{{ selectedCount === 1 ? "" : "s" }} for permanent deletion.
      </p>

      <p v-if="note" class="mb-4">{{ note }}</p>

      <p class="mb-0">
        If any {{ entityLabel }}s are still in use, they cannot be safely deleted. If anything could
        not be deleted, a CSV will be downloaded listing the reasons.
      </p>

      <div v-if="submitting" class="mt-4">
        <progress class="progress is-info" max="100" :value="progressPercentage"></progress>
        <p
          v-if="progressMessage"
          class="has-text-grey is-size-7 mb-0"
          data-test="bulk-delete-progress"
        >
          {{ progressMessage }}
        </p>
        <p v-if="progressEta" class="has-text-grey is-size-7 mb-0" data-test="bulk-delete-eta">
          {{ progressEta }}
        </p>
      </div>

      <div v-if="errorMessage" class="notification is-danger is-light mt-4 mb-0">
        {{ errorMessage }}
      </div>
    </template>

    <template #footer>
      <div class="is-flex is-flex-direction-column is-align-items-stretch is-fullwidth">
        <div class="field mb-3">
          <label class="label" :for="confirmationId">
            Deletion is permanent and cannot be undone. Type <code>permanently delete</code> to
            confirm.
          </label>
          <div class="control">
            <input
              :id="confirmationId"
              v-model="confirmation"
              class="input"
              type="text"
              autocomplete="off"
              placeholder="permanently delete"
              data-test="bulk-delete-confirmation"
            />
          </div>
        </div>
        <div class="is-flex is-justify-content-flex-end bulk-delete-actions">
          <button class="button" :disabled="submitting" @click="close">Cancel</button>
          <button
            class="button is-danger ml-2"
            :class="{ 'is-loading': submitting }"
            :disabled="!canSubmit"
            data-test="bulk-delete-submit"
            @click="submit"
          >
            Permanently delete {{ selectedCount }} {{ entityLabel
            }}{{ selectedCount === 1 ? "" : "s" }}
          </button>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script setup>
  import { computed, nextTick, ref, watch } from "vue";
  import uniqid from "uniqid";

  import { StashedFile } from "~/jose_modern/stashed_file";
  import { requestJson } from "~/recipal-app/controllers/shared/requests.js";

  import BaseModal from "./shared/base-modal.vue";

  const CONFIRMATION = "permanently delete";
  const REQUEST_ERROR = "The request could not be completed.";

  const props = defineProps({
    modelValue: { type: Boolean, default: false },
    entityLabel: { type: String, required: true },
    selectedIds: { type: Array, required: true },
    deleteEndpoint: { type: String, required: true },
    note: { type: String, default: null },
  });

  const emit = defineEmits(["update:modelValue", "deleted", "interrupted"]);

  const selectedIdsAtOpen = ref([]);
  const confirmation = ref("");
  const submitting = ref(false);
  const errorMessage = ref("");
  const progressMessage = ref("");
  const progressPercentage = ref(null);
  const progressEta = ref("");
  let accepted = false;

  const instanceId = Math.random().toString(36).slice(2, 9);
  const titleId = `bulk-delete-title-${instanceId}`;
  const confirmationId = `bulk-delete-confirmation-${instanceId}`;

  const selectedCount = computed(() => selectedIdsAtOpen.value.length);
  const canSubmit = computed(
    () => !submitting.value && selectedCount.value > 0 && confirmation.value === CONFIRMATION
  );

  function reset() {
    accepted = false;
    selectedIdsAtOpen.value = [];
    confirmation.value = "";
    submitting.value = false;
    errorMessage.value = "";
    progressMessage.value = "";
    progressPercentage.value = null;
    progressEta.value = "";
  }

  async function postJson(url, body) {
    const { response, data } = await requestJson(url, {
      method: "POST",
      body: JSON.stringify(body),
    });
    if (!data) throw new Error(REQUEST_ERROR);
    if (!response.ok) throw new Error(data.error || REQUEST_ERROR);
    return data;
  }

  // Every stashed file on the page fires this event, so match the token before reading it.
  function trackProgress(token) {
    const handler = (event) => {
      if (event.token !== token || event.status !== "processing") return;

      const percentage = event.progressPercentage;
      if (typeof percentage === "number") progressPercentage.value = percentage;

      // Skip the last progress message, since the page shows it in a Flash once the modal closes.
      if (typeof percentage === "number" && percentage >= 100) return;

      const share = typeof percentage === "number" ? ` (${percentage.toFixed()}%)` : "";
      progressMessage.value = `${event.progressMessage || ""}${share}`;
      progressEta.value = event.estimatedTimeRemaining || "";
    };
    document.addEventListener("stashed-file:progress", handler);
    return () => document.removeEventListener("stashed-file:progress", handler);
  }

  async function submit() {
    if (!canSubmit.value) return;

    submitting.value = true;
    errorMessage.value = "";
    let stopTracking = null;
    try {
      const result = await postJson(props.deleteEndpoint, {
        "record_ids": selectedIdsAtOpen.value,
        "confirmation": confirmation.value,
        "stashed-file-client-id": uniqid(),
      });
      if (typeof result.status_url !== "string" || !result.status_url) {
        throw new Error(REQUEST_ERROR);
      }
      accepted = true;
      stopTracking = trackProgress(result.token);
      const completion = await StashedFile.download({
        path: result.status_url,
        suppressReadyFlash: true,
      });
      close();
      await nextTick();
      await nextTick();
      emit("deleted", completion.progressMessage);
    } catch (error) {
      errorMessage.value = error.message || `Failed to delete ${props.entityLabel}s.`;
      // A failed job can still delete earlier batches, so tell the parent to refresh its list.
      if (accepted) emit("interrupted");
    } finally {
      if (stopTracking) stopTracking();
      submitting.value = false;
    }
  }

  function close() {
    reset();
    emit("update:modelValue", false);
  }

  function handleVisibilityChange(value) {
    if (!value && !submitting.value) close();
  }

  watch(
    () => props.modelValue,
    (isOpen) => {
      reset();
      if (isOpen) selectedIdsAtOpen.value = [...props.selectedIds];
    },
    { immediate: true }
  );
</script>

<style scoped>
  .bulk-delete-actions {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .bulk-delete-actions > .ml-2 {
    margin-left: 0 !important;
  }

  /* Bulma buttons don't wrap by default, so a long label would overflow on narrow screens. */
  .bulk-delete-actions .button {
    min-width: 0;
    white-space: normal;
    height: auto;
  }
</style>

<!-- We can't use scoped styles here because BaseModal's root is a Teleport. -->
<style>
  .bulk-delete-modal {
    width: min(640px, calc(100vw - 2rem));
  }

  .bulk-delete-modal .modal-card-foot {
    /* These colors come from Bulma's is-danger is-light. */
    background-color: #feecf0;
    color: #cc0f35;
  }

  .bulk-delete-modal .modal-card-title {
    flex-shrink: 1;
    min-width: 0;
    padding-right: 1rem;
  }
</style>
